import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useEffect,
} from "react";
import { useLocation } from "react-router-dom";

interface NavigationContextType {
  activeTab?: "menu" | "deals" | "store-info";
  categories?: { id: number; name: string }[];
  activeCategory?: number;
  onCategoryChange?: (index: number) => void;
  setNavigationProps: (props: Partial<NavigationContextType>) => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(
  undefined,
);

export const NavigationProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<"menu" | "deals" | "store-info">();
  const [categories, setCategories] = useState<{ id: number; name: string }[]>(
    [],
  );
  const [activeCategory, setActiveCategory] = useState<number>(0);
  const [onCategoryChange, setOnCategoryChange] = useState<
    ((index: number) => void) | undefined
  >();

  // Auto-clear categories when navigating away from menu page
  useEffect(() => {
    if (location.pathname !== "/menu") {
      setCategories([]);
      setActiveCategory(0);
      setOnCategoryChange(undefined);
    }
  }, [location.pathname]);

  const setNavigationProps = useCallback(
    (props: Partial<NavigationContextType>) => {
      if (props.activeTab !== undefined) setActiveTab(props.activeTab);
      if (props.categories !== undefined) setCategories(props.categories);
      if (props.activeCategory !== undefined)
        setActiveCategory(props.activeCategory);
      if (props.onCategoryChange !== undefined) {
        setOnCategoryChange(() => props.onCategoryChange);
      }
    },
    [],
  );

  return (
    <NavigationContext.Provider
      value={{
        activeTab,
        categories,
        activeCategory,
        onCategoryChange,
        setNavigationProps,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error("useNavigation must be used within a NavigationProvider");
  }
  return context;
};
